// src/hooks/useAnnouncements.js
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { fetchAnnouncements, refreshAnnouncementsCache } from "../apiService";

/**
 * User-scoped announcements hook:
 * - Always shows all announcements returned by the endpoint
 * - Tracks "read" state per user (localStorage key includes portalEndCustomerId)
 * - Badge count = unread announcements count
 */
export const useAnnouncements = ({
  limit = 100,
  offset = 0,
  useCache = true,
  pollInterval = 0,
  autoFetch = true,
  // optionally pass a customerId in; if omitted we read from localStorage
  customerId,
} = {}) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // "read" IDs for THIS user
  const [readIds, setReadIds] = useState(new Set());

  const pollTimeoutRef = useRef(null);

  // Resolve the current user's ID (scoping key)
  const resolvedCustomerId = useMemo(() => {
    // If caller provided customerId, prefer it
    if (customerId !== undefined && customerId !== null && `${customerId}`.trim() !== "") {
      return `${customerId}`;
    }
    // fallback to what you already store on login
    return localStorage.getItem("portalEndCustomerId") || "anonymous";
  }, [customerId]);

  // Unique storage key per user
  const readKey = useMemo(() => {
    return `readAnnouncements:${resolvedCustomerId}`;
  }, [resolvedCustomerId]);

  // Load read IDs whenever the user changes (or on mount)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(readKey);
      if (stored) {
        const arr = JSON.parse(stored);
        setReadIds(new Set(Array.isArray(arr) ? arr : []));
      } else {
        setReadIds(new Set());
      }
    } catch (e) {
      console.error("Error loading read announcements:", e);
      setReadIds(new Set());
    }
  }, [readKey]);

  // Persist read IDs for current user
  const persistReadIds = useCallback(
    (set) => {
      try {
        localStorage.setItem(readKey, JSON.stringify([...set]));
      } catch (e) {
        console.error("Error saving read announcements:", e);
      }
    },
    [readKey]
  );

  // Fetch announcements
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetchAnnouncements({ offset, limit, useCache });

if (res?.success) {
  const list = Array.isArray(res.data) ? res.data : [];

  const sorted = [...list].sort((a, b) => {
    const da = new Date(a?.date_created || 0).getTime();
    const db = new Date(b?.date_created || 0).getTime();
    return db - da; // newest first
  });

  setAnnouncements(sorted);
} else {
  setAnnouncements([]);
  setError(res?.message || "Failed to load announcements");
}

    } catch (err) {
      console.error("Error fetching announcements:", err);
      setAnnouncements([]);
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [limit, offset, useCache]);

  // Initial fetch
  useEffect(() => {
    if (autoFetch) fetchData();
  }, [autoFetch, fetchData]);

  // Polling
  useEffect(() => {
    if (pollInterval > 0) {
      const poll = () => {
        fetchData();
        pollTimeoutRef.current = setTimeout(poll, pollInterval);
      };

      pollTimeoutRef.current = setTimeout(poll, pollInterval);

      return () => {
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      };
    }
  }, [pollInterval, fetchData]);

  // Mark single announcement as read
  const markRead = useCallback(
    (announcementId) => {
      if (announcementId === undefined || announcementId === null) return;

      setReadIds((prev) => {
        const next = new Set(prev);
        next.add(announcementId);
        persistReadIds(next);
        return next;
      });
    },
    [persistReadIds]
  );

  // Mark all announcements (currently loaded) as read
  const markAllRead = useCallback(() => {
    const next = new Set(announcements.map((a) => a.id).filter((id) => id !== undefined && id !== null));
    setReadIds(next);
    persistReadIds(next);
  }, [announcements, persistReadIds]);

  // Check if an announcement is read
  const isRead = useCallback(
    (announcementId) => readIds.has(announcementId),
    [readIds]
  );

  // Clear read state (for current user)
  const clearRead = useCallback(() => {
    setReadIds(new Set());
    try {
      localStorage.removeItem(readKey);
    } catch (e) {
      console.error("Error clearing read announcements:", e);
    }
  }, [readKey]);

  // Refresh with cache bust (service decides what cache means)
  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await refreshAnnouncementsCache({ offset, limit });

if (res?.success) {
  const list = Array.isArray(res.data) ? res.data : [];

  const sorted = [...list].sort((a, b) => {
    const da = new Date(a?.date_created || 0).getTime();
    const db = new Date(b?.date_created || 0).getTime();
    return db - da; // newest first
  });

  setAnnouncements(sorted);
} else {
  setAnnouncements([]);
  setError(res?.message || "Failed to load announcements");
}

    } catch (err) {
      console.error("Error refreshing announcements:", err);
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  // Badge count: unread announcements
  const unreadCount = useMemo(() => {
    return announcements.reduce((count, a) => (readIds.has(a.id) ? count : count + 1), 0);
  }, [announcements, readIds]);

  return {
    announcements,          // always all announcements from endpoint
    loading,
    error,
    refresh,

    // read-state API
    markRead,
    markAllRead,
    isRead,
    readIds,
    clearRead,

    // counts
    totalCount: announcements.length,
    unreadCount,

    // useful for debugging / UI
    customerId: resolvedCustomerId,
    readKey,
  };
};

export default useAnnouncements;

